import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { marketTicksService } from '../../services/metalguard';
import PrimaryNavBar from 'components/ui/PrimaryNavBar';
import Icon from 'components/AppIcon';
import AggregatedStockTable from './components/AggregatedStockTable';
import SellFromStockModal from './components/SellFromStockModal';
import RecentSalesTable from './components/RecentSalesTable';

function buildAggregatedGroups(batches) {
  const map = new Map();

  for (const batch of batches) {
    const wr = batch?.weight_remaining != null
      ? parseFloat(batch?.weight_remaining)
      : parseFloat(batch?.weight_t) * (parseFloat(batch?.yield_actual ?? batch?.yield_est ?? 90) / 100);

    if (isNaN(wr) || wr <= 0.0001) continue;

    const key = `${batch?.metal_name}||${batch?.category_name}`;
    if (!map?.has(key)) {
      map?.set(key, {
        metal_name: batch?.metal_name,
        category_name: batch?.category_name,
        batches: [],
        total_weight_remaining: 0,
        weighted_yield_sum: 0,
        weighted_purchase_sum: 0,
        batch_count: 0,
      });
    }
    const group = map?.get(key);
    group?.batches?.push(batch);
    group.total_weight_remaining += wr;
    group.batch_count += 1;

    const yieldVal = batch?.yield_actual ?? batch?.yield_est;
    if (yieldVal != null) {
      group.weighted_yield_sum += parseFloat(yieldVal) * wr;
    }
    if (batch?.purchase_price != null) {
      group.weighted_purchase_sum += parseFloat(batch?.purchase_price) * wr;
    }
  }

  return Array.from(map?.values())?.map(g => ({
    ...g,
    avg_yield: g?.total_weight_remaining > 0 && g?.weighted_yield_sum > 0
      ? g?.weighted_yield_sum / g?.total_weight_remaining
      : null,
    avg_purchase_price: g?.total_weight_remaining > 0 && g?.weighted_purchase_sum > 0
      ? g?.weighted_purchase_sum / g?.total_weight_remaining
      : null,
  }))?.sort((a, b) => {
    const metalCmp = (a?.metal_name || '')?.localeCompare(b?.metal_name || '', 'es');
    if (metalCmp !== 0) return metalCmp;
    return (a?.category_name || '')?.localeCompare(b?.category_name || '', 'es');
  });
}

export default function StockSales() {
  const { user, loading: authLoading } = useAuth();
  const [batches, setBatches] = useState([]);
  const [benchmarks, setBenchmarks] = useState([]);
  const [recentSales, setRecentSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [salesLoading, setSalesLoading] = useState(true);
  const [sellModal, setSellModal] = useState(null); // { group, benchmark, recoverableValue }
  const [lastSync, setLastSync] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.id) return;

    const loadAll = async () => {
      setLoading(true);
      try {
        // Load batches — explicitly exclude Completado and Cancelado
        const { data: batchData, error: batchError } = await supabase
          ?.from('batches')
          ?.select('*')
          ?.eq('user_id', user?.id)
          ?.in('batch_status', ['En Espera', 'Procesando', 'Listo para Venta'])
          ?.order('created_at', { ascending: true });

        if (!batchError) {
          setBatches(batchData || []);
          const groups = buildAggregatedGroups(batchData || []);
          console.log('[stock-sales] batches loaded:', (batchData || [])?.length, '| groups computed:', groups?.length, groups?.map(g => g?.metal_name + '/' + g?.category_name));
        } else {
          console.error('[stock-sales] Error loading batches:', batchError);
        }

        // Load benchmarks
        try {
          const { data: bmData } = await marketTicksService?.getLatest();
          setBenchmarks(bmData || []);
        } catch (_) {}

        setLastSync(new Date());
      } finally {
        setLoading(false);
      }
    };

    const loadRecentSales = async () => {
      setSalesLoading(true);
      try {
        const { data: salesData, error: salesError } = await supabase
          ?.from('stock_sales')
          ?.select('*')
          ?.eq('user_id', user?.id)
          ?.order('sold_at', { ascending: false })
          ?.limit(20);

        if (salesError) throw salesError;

        if (salesData?.length > 0) {
          const saleIds = salesData?.map(s => s?.id);
          const { data: allocData } = await supabase
            ?.from('stock_sale_allocations')
            ?.select('*')
            ?.in('stock_sale_id', saleIds);

          const allocMap = new Map();
          for (const alloc of (allocData || [])) {
            if (!allocMap?.has(alloc?.stock_sale_id)) allocMap?.set(alloc?.stock_sale_id, []);
            allocMap?.get(alloc?.stock_sale_id)?.push(alloc);
          }

          const enriched = salesData?.map(s => ({
            ...s,
            allocations: allocMap?.get(s?.id) || [],
          }));
          setRecentSales(enriched);
        } else {
          setRecentSales([]);
        }
      } catch (err) {
        console.error('[stock-sales] Error loading recent sales:', err);
      } finally {
        setSalesLoading(false);
      }
    };

    loadAll();
    loadRecentSales();
  }, [user?.id, authLoading]);

  // Real-time subscription for batches — listens for INSERT and UPDATE events
  useEffect(() => {
    if (!user?.id) return;

    const reloadBatches = async () => {
      setRefreshing(true);
      try {
        const { data, error } = await supabase
          ?.from('batches')
          ?.select('*')
          ?.eq('user_id', user?.id)
          ?.in('batch_status', ['En Espera', 'Procesando', 'Listo para Venta'])
          ?.order('created_at', { ascending: true });
        if (!error) {
          setBatches(data || []);
          setLastSync(new Date());
        }
      } finally {
        setRefreshing(false);
      }
    };

    const channel = supabase
      ?.channel(`stock_sales_batches_${user?.id}`)
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'batches',
        filter: `user_id=eq.${user?.id}`,
      }, () => reloadBatches())
      ?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'batches',
        filter: `user_id=eq.${user?.id}`,
      }, (payload) => {
        const changed = payload?.new;
        const old = payload?.old;
        const weightChanged = changed?.weight_remaining !== old?.weight_remaining;
        const statusChanged = changed?.batch_status !== old?.batch_status;
        if (weightChanged || statusChanged) reloadBatches();
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  }, [user?.id]);

  const handleSaleSuccess = useCallback(() => {
    if (!user?.id) return;
    // Reload batches and sales after a successful sale
    supabase
      ?.from('batches')
      ?.select('*')
      ?.eq('user_id', user?.id)
      ?.in('batch_status', ['En Espera', 'Procesando', 'Listo para Venta'])
      ?.order('created_at', { ascending: true })
      ?.then(({ data, error }) => {
        if (!error) setBatches(data || []);
      });

    supabase
      ?.from('stock_sales')
      ?.select('*')
      ?.eq('user_id', user?.id)
      ?.order('sold_at', { ascending: false })
      ?.limit(20)
      ?.then(async ({ data: salesData, error: salesError }) => {
        if (salesError || !salesData?.length) { setRecentSales(salesData || []); return; }
        const saleIds = salesData?.map(s => s?.id);
        const { data: allocData } = await supabase
          ?.from('stock_sale_allocations')
          ?.select('*')
          ?.in('stock_sale_id', saleIds);
        const allocMap = new Map();
        for (const alloc of (allocData || [])) {
          if (!allocMap?.has(alloc?.stock_sale_id)) allocMap?.set(alloc?.stock_sale_id, []);
          allocMap?.get(alloc?.stock_sale_id)?.push(alloc);
        }
        setRecentSales(salesData?.map(s => ({ ...s, allocations: allocMap?.get(s?.id) || [] })));
      });

    setLastSync(new Date());
  }, [user?.id]);

  const handleCancelSale = useCallback(async (sale) => {
    if (!sale?.id || !user?.id) return;
    setCancellingId(sale?.id);
    try {
      // 1. Fetch allocations for this sale
      const { data: allocations, error: allocErr } = await supabase
        ?.from('stock_sale_allocations')
        ?.select('batch_id, weight_allocated')
        ?.eq('stock_sale_id', sale?.id);

      if (allocErr) throw allocErr;

      // 2. Restore weight_remaining on each affected batch
      for (const alloc of (allocations || [])) {
        // Fetch current weight_remaining
        const { data: batchRow, error: fetchErr } = await supabase
          ?.from('batches')
          ?.select('weight_remaining, weight_t, batch_status')
          ?.eq('id', alloc?.batch_id)
          ?.single();

        if (fetchErr) continue;

        const current = batchRow?.weight_remaining != null
          ? parseFloat(batchRow?.weight_remaining)
          : parseFloat(batchRow?.weight_t ?? 0);

        const restored = current + parseFloat(alloc?.weight_allocated);
        const existingStatus = batchRow?.batch_status;

        await supabase
          ?.from('batches')
          ?.update({ weight_remaining: restored, batch_status: restored > 0 ? 'Listo para Venta' : existingStatus })
          ?.eq('id', alloc?.batch_id);
      }

      // 3. Delete the stock_sale (allocations cascade-delete via FK)
      const { error: deleteErr } = await supabase
        ?.from('stock_sales')
        ?.delete()
        ?.eq('id', sale?.id)
        ?.eq('user_id', user?.id);

      if (deleteErr) throw deleteErr;

      // 4. Refresh data
      handleSaleSuccess();
    } catch (err) {
      console.error('[stock-sales] Error cancelling sale:', err);
    } finally {
      setCancellingId(null);
    }
  }, [user?.id]);

  const groups = buildAggregatedGroups(batches);

  // Summary stats
  const totalAvailableWeight = groups?.reduce((sum, g) => sum + g?.total_weight_remaining, 0);
  const totalCategories = groups?.length;

  const getBenchmarkForMetal = (metalName) => {
    if (!metalName || !benchmarks?.length) return null;
    const METAL_NAME_TO_SYMBOL = {
      'cobre': 'LME_CU',
      'aluminio': 'LME_AL',
      'zinc': 'LME_ZN',
      'plomo': 'LME_PB',
      'níquel': 'LME_NI',
      'niquel': 'LME_NI',
      'estaño': 'LME_SN',
      'estano': 'LME_SN',
      'acero inox': 'REG_INOX',
      'inox': 'REG_INOX',
      'hierro': 'REG_FE',
      'acero': 'REG_FE',
      'latón': 'REG_LAT',
      'laton': 'REG_LAT',
      'bronce': 'MAN_BRZ',
    };
    const key = metalName?.toLowerCase()?.trim();
    const symbol = METAL_NAME_TO_SYMBOL?.[key];
    if (symbol) {
      const tick = benchmarks?.find(t => t?.symbol === symbol);
      return tick?.value ? parseFloat(tick?.value) : null;
    }
    return null;
  };

  const totalRecoverableValue = groups?.reduce((sum, g) => {
    const bm = getBenchmarkForMetal(g?.metal_name);
    if (bm == null || g?.avg_yield == null) return sum;
    return sum + bm * (g?.avg_yield / 100) * g?.total_weight_remaining;
  }, 0);

  const lastSyncStr = lastSync
    ? lastSync?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryNavBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(5,150,105,0.12)' }}
              >
                <Icon name="TrendingUp" size={18} color="#059669" strokeWidth={2} />
              </div>
              <h1
                className="font-bold text-foreground"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '1.375rem', letterSpacing: '-0.01em' }}
              >
                Venta de Stock Agregado
              </h1>
            </div>
            <p className="text-sm text-muted-foreground ml-11.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
              Vende cantidades parciales o totales de stock por categoría con análisis de rentabilidad por lotes
            </p>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Live benchmark indicator */}
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border"
              style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}
            >
              <div className="relative">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                <span className="absolute inset-0 inline-block w-2 h-2 rounded-full bg-green-500 animate-ping opacity-60" />
              </div>
              <span className="text-xs text-muted-foreground" style={{ fontFamily: '"JetBrains Mono", monospace', fontVariantNumeric: 'tabular-nums' }}>
                LME {lastSyncStr || '—'}
              </span>
            </div>

            <button
              onClick={() => {
                if (groups?.length > 0) {
                  const first = groups?.[0];
                  const bm = getBenchmarkForMetal(first?.metal_name);
                  const rv = bm != null && first?.avg_yield != null
                    ? bm * (first?.avg_yield / 100) * first?.total_weight_remaining
                    : null;
                  setSellModal({ group: first, benchmark: bm, recoverableValue: rv });
                }
              }}
              disabled={groups?.length === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}
            >
              <Icon name="Plus" size={16} color="#fff" strokeWidth={2.5} />
              Nueva Venta
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {!loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
            {[
              {
                label: 'Categorías con stock',
                value: totalCategories,
                unit: '',
                icon: 'Layers',
                color: 'rgba(99,102,241,0.1)',
                iconColor: '#6366f1',
              },
              {
                label: 'Peso total disponible',
                value: totalAvailableWeight?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                unit: 't',
                icon: 'Scale',
                color: 'rgba(59,130,246,0.1)',
                iconColor: '#3b82f6',
              },
              {
                label: 'Valor recuperable total',
                value: totalRecoverableValue > 0
                  ? totalRecoverableValue?.toLocaleString('es-ES', { maximumFractionDigits: 0 })
                  : '—',
                unit: totalRecoverableValue > 0 ? '€' : '',
                icon: 'Euro',
                color: 'rgba(5,150,105,0.1)',
                iconColor: '#059669',
              },
            ]?.map(({ label, value, unit, icon, color, iconColor }) => (
              <div
                key={label}
                className="bg-card border border-border rounded-xl p-4 flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: color }}>
                  <Icon name={icon} size={18} color={iconColor} strokeWidth={1.75} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground truncate" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{label}</p>
                  <p className="text-lg font-mono font-bold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {value}{unit ? ` ${unit}` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Aggregated Stock Table */}
        <div className="mb-8">
          <AggregatedStockTable
            groups={groups}
            benchmarks={benchmarks}
            loading={loading}
            refreshing={refreshing}
            onSell={(group, benchmark, recoverableValue) =>
              setSellModal({ group, benchmark, recoverableValue })
            }
          />
        </div>

        {/* Recent Sales */}
        <RecentSalesTable
          sales={recentSales}
          loading={salesLoading}
          onCancelSale={handleCancelSale}
          cancellingId={cancellingId}
        />
      </main>
      {/* Sell Modal */}
      {sellModal && (
        <SellFromStockModal
          group={sellModal?.group}
          benchmark={sellModal?.benchmark}
          recoverableValue={sellModal?.recoverableValue}
          onClose={() => setSellModal(null)}
          onSuccess={handleSaleSuccess}
        />
      )}
    </div>
  );
}
